/**
 * @docs: docs/core/architecture/SCOUT_GATEWAY_REMOTE.md
 *
 * /sg/join?d=<device>&c=<code>: what the QR code in SG's Tools › Remote access
 * opens. Signed in (AuthGuard), it asks the SG's owner to let this person in;
 * the code changes every 5 minutes, so having it shows they are on board.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../services/api';

type State = { kind: 'working' } | { kind: 'requested' } | { kind: 'member'; role: string } | { kind: 'error'; message: string };

export function SgJoinPage() {
  const [params] = useSearchParams();
  const device = params.get('d') ?? '';
  const code = params.get('c') ?? '';
  const [state, setState] = useState<State>({ kind: 'working' });
  const asked = useRef(false);

  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    if (!device || !code) { setState({ kind: 'error', message: 'This link is incomplete. Scan the QR code on the SG screen again.' }); return; }
    api.joinGateway(device, code)
      .then((r) => setState(r.status === 'member' ? { kind: 'member', role: r.role } : { kind: 'requested' }))
      .catch((e) => setState({ kind: 'error', message: e?.response?.data?.error ?? 'That did not work. Scan the QR code on the SG screen again.' }));
  }, [device, code]);

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-slate-800/60 border border-slate-700 rounded-2xl p-6 space-y-3">
        <h1 className="text-xl font-bold text-white">Join a Scout Gateway</h1>
        {state.kind === 'working' && <p className="text-slate-300">Asking the boat’s owner…</p>}
        {state.kind === 'requested' && (
          <>
            <p className="text-slate-200">Done. The boat’s owner has been asked to let you in.</p>
            <p className="text-slate-400 text-sm">Once they approve, the boat appears under My Devices › Shared with you, with Open SG.</p>
          </>
        )}
        {state.kind === 'member' && (
          <>
            <p className="text-slate-200">{state.role === 'owner' ? 'This is your SG.' : 'You already have access to this SG.'}</p>
            <a href={api.gatewayRemoteAppUrl(device)} target="_blank" rel="noopener"
              className="inline-block px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white">Open SG</a>
          </>
        )}
        {state.kind === 'error' && <p className="text-amber-300">{state.message}</p>}
        <Link to="/dashboard/devices" className="block text-cyan-400 hover:underline text-sm">My Devices</Link>
      </div>
    </div>
  );
}

export default SgJoinPage;
